import React, { useState, useEffect, useMemo } from 'react'
import tw, { styled } from 'twin.macro'
import useLocale from '../hooks/useLocale'
import { Link, useStaticQuery, graphql } from 'gatsby'
import Img from 'gatsby-image'
import { BLOCKS } from '@contentful/rich-text-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import filterLocale from './utils/filter-locale'
import Meta from '../components/meta'
import img1WebP from '../images/maytronics_dolphin_m600_robotic_pool_cleaner_climbing_pool_wall_underwater.webp'
import PropTypes from 'prop-types'
import '../components/fonts/flexo.css'
const windowGlobal = typeof window !== 'undefined' && window

const HeroSection = styled.section`
	${tw`flex items-center justify-center px-4`}
`

const HeroWrapper = styled.div`
	${tw`relative w-full overflow-hidden`}

	@media (min-width: 640px) {
		height: 100vh;
		height: 100dvh;
	}
`

const ButtonOverlay = styled.div`
	${tw`hidden sm:flex absolute inset-0 z-10 items-start`}
	padding-top: 55vh;
	padding-left: 6.8%;
`

const Container = tw.div`relative`

const MobileButtons = styled.div`
	${tw`sm:hidden flex flex-col items-center gap-2 px-4 py-4`}
`

const MobileButtonRow = tw.div`flex flex-row flex-nowrap items-center justify-center gap-3`

const PrimaryButton = styled(Link)`
	${tw`inline-flex items-center justify-center bg-white text-black font-black rounded-full hover:bg-gray-200 transition duration-300 shadow-lg`};
	font-size: clamp(0.8rem, 1.4vw, 1.25rem);
	padding: clamp(0.5rem, 1.1vw, 1rem) clamp(1.5rem, 3.9vw, 3.5rem);
`

const OutlineButton = styled(Link)`
	${tw`inline-flex items-center justify-center text-white font-black border-2 border-white rounded-full hover:bg-white hover:text-black transition duration-300 shadow-lg`};
	font-size: clamp(0.8rem, 1.4vw, 1.25rem);
	padding: clamp(0.5rem, 1.1vw, 1rem) clamp(0.75rem, 1.5vw, 1.25rem);
`

const SmallOutlineButton = styled(Link)`
	${tw`inline-flex items-center justify-center text-white font-black border-2 border-white rounded-full hover:bg-white hover:text-black transition duration-300 shadow-lg`};
	font-size: clamp(0.7rem, 1.1vw, 1.125rem);
	padding: clamp(0.4rem, 0.7vw, 0.625rem) clamp(1.5rem, 3.9vw, 3.5rem);
`

const MobilePrimaryButton = styled(Link)`
	${tw`inline-flex items-center justify-center bg-black text-white font-semibold cursor-pointer`}
	border-radius: 9999px;
	font-size: 0.8rem;
	text-decoration: none;
	padding: 0.5rem 1.75rem;
	min-height: 38px;
`

const MobileOutlineButton = styled(Link)`
	${tw`inline-flex items-center justify-center font-semibold cursor-pointer`}
	background: transparent;
	color: black;
	border: 2px solid black;
	border-radius: 9999px;
	font-size: 0.8rem;
	text-decoration: none;
	padding: 0.5rem 1.75rem;
	min-height: 38px;
`

const ButtonRow = styled.div`
	${tw`flex flex-row flex-nowrap items-center justify-start`}
	gap: clamp(0.5rem, 1vw, 1.5rem);
`

const ButtonRowSecond = tw.div`flex flex-row items-center justify-start mt-4`

const Hero3 = ({ locale }) => {
	const { firstBanner } = useStaticQuery(graphql`
		query homeHero3BannersQuery {
			firstBanner: allContentfulBanner(
				filter: { contentful_id: { eq: "4ID36kjlnoDEOxdZmDErdv" } }
			) {
				edges {
					node {
						backgroundImage {
							file {
								url
							}
							fluid(maxWidth: 1920, quality: 80) {
								...GatsbyContentfulFluid_withWebp
							}
						}
						mainActionText
						mainActionUrl
						secondActionText
						secondActionUrl
						thirdActionText
						thirdActionUrl
						node_locale
						hook
						description {
							json
						}
					}
				}
			}
		}
	`)
	const [{ node: first }] = filterLocale(firstBanner.edges, locale)

	const [state, setState] = useState({
		pathname: '',
	})

	const options = useMemo(
		() => ({
			renderNode: {
				[BLOCKS.PARAGRAPH]: (node, children) => <p css={tw`m-0`}>{children}</p>,
			},
		}),
		[]
	)

	useEffect(() => {
		if (windowGlobal) {
			const path = windowGlobal.location.pathname
			setState(prevState => ({ ...prevState, pathname: path }))
		}
	}, [])

	const { globalLocale } = useLocale(state.pathname)

	const checkUrl =
		globalLocale !== 'en-AU'
			? first.secondActionUrl.includes(globalLocale)
			: null

	const checkThirdUrl =
		globalLocale !== 'en-AU'
			? first.thirdActionUrl?.includes(globalLocale)
			: null

	const bannerDescription =
		first.description?.json?.content?.[0]?.content?.[0]?.value ||
		'Discover our robotic pool cleaners and vacuums for effortless pool maintenance.'

	const showSecondAction = checkUrl === null || checkUrl !== false
	const showThirdAction =
		first.thirdActionText &&
		first.thirdActionUrl &&
		(checkThirdUrl === null || checkThirdUrl !== false)

	return (
		<>
			<Meta title="Robotic Pool Cleaners & Vacuums" description={bannerDescription}>
				<meta
					name="keywords"
					content="robotic pool cleaners, pool vacuums, automatic pool cleaners, pool maintenance, swimming pool equipment"
				/>
				<meta property="og:image" content={img1WebP} />
				<meta property="og:type" content="product" />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:image" content={img1WebP} />
				<link rel="preload" as="image" href={img1WebP} fetchPriority="high" type="image/webp" />
			</Meta>

			<HeroWrapper>
				<Img
					fluid={first.backgroundImage?.fluid}
					loading="eager"
					css={tw`w-full h-auto sm:h-full`}
					imgStyle={{ objectFit: 'cover', objectPosition: 'left center' }}
				/>

				{/* Desktop overlay buttons */}
				<ButtonOverlay>
					<Container>
						<ButtonRow>
							<PrimaryButton
								to={'/' + first.mainActionUrl}
								aria-label={first.mainActionText}
							>
								{first.mainActionText}
							</PrimaryButton>

							{showSecondAction && (
								<OutlineButton
									to={'/' + first.secondActionUrl}
									aria-label={first.secondActionText}
								>
									{first.secondActionText}
								</OutlineButton>
							)}
						</ButtonRow>

						{showThirdAction && (
							<ButtonRowSecond>
								<SmallOutlineButton
									to={'/' + first.thirdActionUrl}
									aria-label={first.thirdActionText}
								>
									{first.thirdActionText}
								</SmallOutlineButton>
							</ButtonRowSecond>
						)}
					</Container>
				</ButtonOverlay>
			</HeroWrapper>

			{/* Mobile buttons rendered below the image */}
			<MobileButtons>
				<MobileButtonRow>
					<MobilePrimaryButton
						to={'/' + first.mainActionUrl}
						aria-label={first.mainActionText}
					>
						{first.mainActionText}
					</MobilePrimaryButton>

					{showSecondAction && (
						<MobileOutlineButton
							to={'/' + first.secondActionUrl}
							aria-label={first.secondActionText}
						>
							{first.secondActionText}
						</MobileOutlineButton>
					)}
				</MobileButtonRow>

				{showThirdAction && (
					<MobileOutlineButton
						to={'/' + first.thirdActionUrl}
						aria-label={first.thirdActionText}
					>
						{first.thirdActionText}
					</MobileOutlineButton>
				)}
			</MobileButtons>
		</>
	)
}

Hero3.propTypes = {
	locale: PropTypes.string.isRequired,
}

export default Hero3
